import { Button, ButtonProps } from '@insight-health-ai/roseate/components';
import { RefreshCw01 } from '@insight-health-ai/roseate/icons';
import classNames from 'classnames';
import styled from '@emotion/styled';

type ErrorScreenProps = {
  style?: React.CSSProperties;
  className?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  retryButton?: Pick<
    ButtonProps,
    'label' | 'onClick' | 'appearance' | 'disabled' | 'loading' | 'icon'
  >;
  errorType?: 'network' | 'server' | 'permission' | 'notFound' | 'generic';
};

const StyledSvg = styled.svg`
  .connector {
    fill: rgb(var(--roseate-color-primary));
  }

  .cable {
    stroke: rgb(var(--roseate-color-primary));
  }

  .background-circle {
    fill: rgb(var(--roseate-color-primary) / 0.08);
  }

  .prong {
    fill: #2d3748;
  }
`;

function ErrorImage({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <StyledSvg
      viewBox="90 80 220 140"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <circle className="background-circle" cx="200" cy="150" r="70" />

      {/* Left plug */}
      <g transform="translate(160, 150)">
        <path
          className="cable"
          d="M 0 0 Q -30 -20 -60 -15"
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
        />
        <rect
          className="connector"
          x="-10"
          y="-16"
          width="28"
          height="32"
          rx="4"
        />
        <rect className="prong" x="18" y="-6" width="14" height="5" rx="1" />
        <rect className="prong" x="18" y="1" width="14" height="5" rx="1" />
      </g>

      {/* Right socket */}
      <g transform="translate(240, 150)">
        <path
          className="cable"
          d="M 0 0 Q 30 -20 60 -15"
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
        />
        <rect
          className="connector"
          x="-18"
          y="-16"
          width="28"
          height="32"
          rx="4"
        />
        <rect className="prong" x="-32" y="-6" width="12" height="4" rx="1" />
        <rect className="prong" x="-32" y="2" width="12" height="4" rx="1" />
      </g>

      {/* Static sparks in the gap */}
      <g transform="translate(200, 150)">
        <path
          d="M -5 0 L -10 -6 L -7 -2 L -11 -8"
          stroke="#fbbf24"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          opacity="0.8"
        />
        <path
          d="M 5 0 L 10 -6 L 7 -2 L 11 -8"
          stroke="#fbbf24"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          opacity="0.8"
        />
        <path
          d="M 0 -3 L -4 -9 L -2 -6 L -5 -11"
          stroke="#f59e0b"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          opacity="0.7"
        />
        <circle cx="-8" cy="-4" r="1.5" fill="#fbbf24" opacity="0.8" />
        <circle cx="8" cy="-4" r="1.5" fill="#fbbf24" opacity="0.8" />
        <circle cx="0" cy="-8" r="1" fill="#f59e0b" opacity="0.7" />
        <circle cx="-5" cy="5" r="1" fill="#fbbf24" opacity="0.6" />
        <circle cx="5" cy="5" r="1" fill="#fbbf24" opacity="0.6" />
      </g>
    </StyledSvg>
  );
}

export const ErrorScreen = ({
  style,
  className,
  title,
  description,
  retryButton,
  errorType = 'generic',
}: ErrorScreenProps) => {
  const getDefaultContent = (type: string) => {
    switch (type) {
      case 'network':
        return {
          title: 'Connection issue',
          description: 'Please check your internet connection and try again.',
        };
      case 'server':
        return {
          title: 'Service temporarily unavailable',
          description:
            'Our servers are experiencing issues. Please try again in a few moments.',
        };
      case 'permission':
        return {
          title: 'Access denied',
          description:
            "You don't have permission to view this content. Please contact your administrator.",
        };
      case 'notFound':
        return {
          title: 'Not found',
          description:
            'The requested information could not be found. It may have been moved or deleted.',
        };
      default:
        return {
          title: 'Oops! Something went wrong',
          description:
            "We've let our engineers know. Please try again or contact support if the issue persists.",
        };
    }
  };

  const defaultContent = getDefaultContent(errorType);
  return (
    <div
      className={classNames(
        'flex h-full w-full flex-col gap-3 overflow-hidden items-center justify-center',
        className
      )}
      style={style}
    >
      <ErrorImage
        className="max-w-[320px]"
        style={{ maxWidth: 'min(320px, 80%)' }}
      />
      <div className="flex flex-col gap-1 items-center justify-center max-w-[480px]">
        <div className="text-xl font-semibold text-center">
          {title || defaultContent.title}
        </div>

        <div className="text-gray-500 text-center">
          {description || defaultContent.description}
        </div>
      </div>
      {retryButton?.onClick && (
        <Button
          variant="outlined"
          label="Retry"
          icon={<RefreshCw01 />}
          {...retryButton}
        />
      )}
    </div>
  );
};
